import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {io, Socket} from 'socket.io-client';
import {RootState} from '../store';
import {notificationApi, NotificationRecord} from '../services/notificationApi';
import {MessagingConversation, MessagingMessage} from '../services/messagingApi';
import {setNotificationUnreadCount} from '../store/slices/notificationSlice';

type RealtimeServerToClientEvents = {
  'messaging:message-created': (payload: {message: MessagingMessage}) => void;
  'messaging:conversation-updated': (payload: {conversation: MessagingConversation}) => void;
  'messaging:messages-read': (payload: {conversationId: string; readerId: string; readAt: string}) => void;
  'presence:user-status-updated': (payload: {userId: string; status: 'online' | 'sleep' | 'offline'}) => void;
  'notifications:created': (payload: {notification: NotificationRecord}) => void;
  'notifications:summary-updated': (payload: {unreadCount: number}) => void;
  'notifications:marked-read': (payload: {notificationId: string; readAt: string; unreadCount: number}) => void;
  'notifications:all-read': (payload: {readAt: string; unreadCount: number}) => void;
};

type RealtimeSocket = Socket<RealtimeServerToClientEvents>;

type RealtimeContextValue = {
  isConnected: boolean;
  reconnectSequence: number;
  socket: RealtimeSocket | null;
};

const RealtimeContext = React.createContext<RealtimeContextValue>({
  isConnected: false,
  reconnectSequence: 0,
  socket: null,
});

const isLocalDevelopmentHost = (hostname: string) => (
  hostname === 'localhost'
  || hostname === '127.0.0.1'
  || hostname === '[::1]'
);

const resolveRealtimeUrl = () => {
  const explicitRealtimeUrl = process.env.REACT_APP_REALTIME_URL?.trim();

  if (explicitRealtimeUrl) {
    return explicitRealtimeUrl;
  }

  const apiUrl = process.env.REACT_APP_API_URL?.trim() || '/api';

  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    try {
      return new URL(apiUrl).origin;
    } catch (error) {
      return window.location.origin;
    }
  }

  // When the API URL is relative, use the current origin so local CRA proxying
  // can carry websocket upgrades to the backend instead of bypassing WSL port proxying.
  return window.location.origin;
};

const shouldUseLocalProxyTransportFallback = () => {
  const explicitRealtimeUrl = process.env.REACT_APP_REALTIME_URL?.trim();

  if (explicitRealtimeUrl) {
    return false;
  }

  const apiUrl = process.env.REACT_APP_API_URL?.trim() || '/api';

  if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
    return false;
  }

  return isLocalDevelopmentHost(window.location.hostname);
};

export const RealtimeProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const [socket, setSocket] = React.useState<RealtimeSocket | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const [reconnectSequence, setReconnectSequence] = React.useState(0);

  React.useEffect(() => {
    if (!token) {
      setSocket((currentSocket) => {
        currentSocket?.removeAllListeners();
        currentSocket?.disconnect();
        return null;
      });
      setIsConnected(false);
      setReconnectSequence(0);
      return;
    }

    let isMounted = true;
    let hasConnectedOnce = false;
    const shouldUseTransportFallback = shouldUseLocalProxyTransportFallback();
    const nextSocket: RealtimeSocket = io(resolveRealtimeUrl(), {
      auth: {
        token,
      },
      ...(shouldUseTransportFallback ? {} : {transports: ['websocket']}),
    });

    const syncUnreadSummary = async () => {
      try {
        const summary = await notificationApi.getUnreadSummary(token);

        if (isMounted) {
          dispatch(setNotificationUnreadCount(summary.unreadCount));
        }
      } catch (error) {
        // Keep the last known count if realtime recovery fails.
      }
    };

    nextSocket.on('connect', () => {
      if (!isMounted) {
        return;
      }

      setIsConnected(true);
      setReconnectSequence((currentSequence) => (
        hasConnectedOnce ? currentSequence + 1 : currentSequence
      ));
      hasConnectedOnce = true;
      void syncUnreadSummary();
    });

    nextSocket.on('disconnect', () => {
      if (isMounted) {
        setIsConnected(false);
      }
    });

    nextSocket.on('connect_error', (error) => {
      if (isMounted) {
        setIsConnected(false);
      }

      console.error('Realtime connection failed:', error.message);
    });

    nextSocket.on('notifications:summary-updated', ({unreadCount}) => {
      dispatch(setNotificationUnreadCount(unreadCount));
    });

    setSocket(nextSocket);

    return () => {
      isMounted = false;
      nextSocket.removeAllListeners();
      nextSocket.disconnect();
      setSocket((currentSocket) => (currentSocket === nextSocket ? null : currentSocket));
      setIsConnected(false);
    };
  }, [dispatch, token]);

  return (
    <RealtimeContext.Provider
      value={{
        isConnected,
        reconnectSequence,
        socket,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = () => React.useContext(RealtimeContext);
